import React, {useEffect, useState} from 'react';
import Header from "../../components/Private/Header";
import {ReactComponent as Arrow} from "../../assets/icons/right.svg";
import {ReactComponent as Radio} from "../../assets/icons/radio.svg";
import {ReactComponent as Card} from "../../assets/icons/card-pos.svg";
import {ReactComponent as Clock} from "../../assets/icons/clock.svg";
import {Button, Spinner} from "@heroui/react";
import {useParams} from "react-router";
import {useAppNavigate} from "../../lib/use-app-navigate";
import {useQuery} from "@tanstack/react-query";
import {request} from "../../lib/request";
import ModalTime from "../../components/Private/ModalTime";
import {usePrintInvoice} from "../../hooks/usePrintInvoice";
import {ReactComponent as FeedbackIcon} from "../../assets/icons/feedback.svg";
import {ReactComponent as TickIcon} from "../../assets/icons/tick.svg";

const canAcceptOrder = (order) => {
    if (!order || typeof order !== 'object' || Array.isArray(order)) {
        return false;
    }

    if (typeof order.can_accept === 'boolean') {
        return order.can_accept;
    }

    if (order.status === 'pending') {
        return true;
    }

    return order.status === 'processing' && order.restaurant_accept === false;
};

const PrivateOrder = () => {
    const navigate = useAppNavigate()
    const [check, setCheck] = useState(false)
    const {id} = useParams()

    useEffect(() => {
        setCheck(false)
    }, [id])

    const {data, isLoading, refetch} = useQuery({
        queryKey: ["restaurant-order", id],
        queryFn: async () => {
            return await request({
                method: "get",
                url: `/restaurant/order/show_order/${id}`,
            })
        }
    })

    const order = data?.data ?? null;
    const noteAcknowledged = !order?.notes || check;
    const showAcceptActions = canAcceptOrder(order);
    const paymentIncomplete = order?.status === 'pending' && order?.can_accept === false;
    const statusConfig = {
        pending: {
            borderColor: "border-r-[10px] border-amber-400",
        },

        processing: {
            borderColor: "border-r-[10px] border-blue-300",
        },

        completed: {
            borderColor: "border-r-[10px] border-blue-600",
        },

        delivery: {
            borderColor: "border-r-[10px] border-teal-500",
        },

        cancelled: {
            borderColor: "border-r-[10px] border-red-500",
        },
        rejected: {
            borderColor: "border-r-[10px] border-red-700",
        },
    };
    const currentStatus = statusConfig[order?.status] || statusConfig.processing;
    const print = usePrintInvoice(order);
    if (isLoading)
        return <div className="w-full min-h-screen center"><Spinner/></div>
    return (
        <>
            <Header title={<Arrow className="rotate-180 cursor-pointer " onClick={() => navigate(-1)}/>}/>
            <div className="flex flex-col gap-5 lg:gap-8 w-full">

                <div className="flex flex-col w-full gap-4 pt-4">
                    <div
                        className={`flex flex-col gap-8 px-8 py-6 w-full border rounded-xl ${currentStatus.borderColor} `}>
                        <div className="flex items-center gap-1">
                            <p className="lg:text-lg text-black font-medium">{order?.full_name}</p>
                            {!!order?.isFirst && <p className="text-Primary-800"> ( اولین سفارش کاربر )</p>}
                        </div>
                        <div className="flex items-center w-full flex-wrap gap-4">
                            <div className="pl-4 lg:border-l border-l-gray-600">
                                <p className="text-sm text-black font-medium">{order?.created}</p>
                            </div>
                            {order?.sending_method === "in_person" && <>
                                <div className="pl-4 lg:border-l border-l-gray-600">
                                    <p className="text-sm text-black font-medium">{order?.mobile}</p>
                                </div>
                                <div className="pl-4 border-l-gray-600">
                                    <p className="text-sm text-black font-medium">تحویل حضوری</p>
                                </div>
                            </>}
                            {order?.showAddress && <>
                                <div className="pl-4 lg:border-l border-l-gray-600">
                                    <p className="text-sm text-black font-medium">{order?.mobile}</p>
                                </div>
                                <div className="pl-4 border-l-gray-600">
                                    <p className="text-sm text-black font-medium">{order?.address}</p>
                                </div>
                            </>}
                        </div>
                    </div>
                </div>


                <div className="flex flex-col gap-4 p-6 border border-gray-300 rounded-xl">
                    <div className="flex items-center justify-center py-4 border-b border-b-gray-300 text-[#353535]">
                        فاکتور ({order?.id})
                    </div>
                    <div className="flex flex-col items-center w-full border-b border-b-gray-300 pb-4">
                        {order?.items?.map((i, index) => {
                            const foodLineTotal = Number(i.food_line_total ?? (Number(i.price) * Number(i.quantity)));
                            const dishLineTotal = Number(i.dish_line_total ?? 0);
                            const dishQuantity = Number(i.dish_quantity ?? 0);
                            const dishUnitPrice = Number(i.dish_price ?? 0);

                            return (
                        <div
                            key={i.id ?? index}
                            className={`grid grid-cols-9 ${index % 2 === 0 ? "bg-[#F9F9F9]" : "bg-[#EDEDED]"} w-full p-4 pr-8`}>
                            <div className="col-span-4 flex my-auto flex-col gap-2">
                                <p className="font-semibold text-[#353535]">{i.name}</p>
                                {dishLineTotal > 0 && (
                                    <div className="flex flex-wrap items-center gap-1 text-sm text-[#353535]">
                                        <span>ظرف</span>
                                        <span className="px-2 text-center text-Primary-950 bg-Primary-50">
                                            {dishQuantity.toLocaleString('fa-IR')}
                                        </span>
                                        <span className="text-xs text-[#717171]">
                                            ({dishUnitPrice.toLocaleString('fa-IR')} × {dishQuantity.toLocaleString('fa-IR')} = {dishLineTotal.toLocaleString('fa-IR')} تومان)
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div
                                className="flex items-center justify-center py-2 bg-Primary-50 max-w-20 w-full text-lg text-center text-Primary-950">{i.quantity}
                            </div>
                            <div className="col-span-4 flex flex-col gap-2">
                                <div className="flex justify-end my-auto gap-1 lg:gap-3">
                                    <p className="font-semibold">{foodLineTotal.toLocaleString('fa-IR')}</p>
                                    <p>تومان</p>
                                </div>
                                {dishLineTotal > 0 && (
                                    <div className="flex justify-end items-center gap-1 lg:gap-3">
                                        <p className="text-xs text-[#717171]">{dishLineTotal.toLocaleString('fa-IR')}</p>
                                        <p className="text-xs text-[#717171]">تومان (ظرف)</p>
                                    </div>
                                )}
                            </div>
                        </div>
                            );
                        })}
                    </div>
                    {!!order?.notes && (
                        <div
                            className={`rest-order-note${check ? ' rest-order-note--confirmed' : ''}${!showAcceptActions ? ' rest-order-note--readonly' : ''}`}
                        >
                            <div className="rest-order-note__accent" aria-hidden="true"/>

                            <div className="rest-order-note__header">
                                <div className="rest-order-note__title-row">
                                    <span className="rest-order-note__icon" aria-hidden="true">
                                        <FeedbackIcon/>
                                    </span>
                                    <div className="rest-order-note__titles">
                                        <h3 className="rest-order-note__title">توضیحات مشتری</h3>
                                        {!showAcceptActions && (
                                            <p className="rest-order-note__subtitle">
                                                یادداشت ثبت‌شده توسط مشتری هنگام سفارش
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {showAcceptActions ? (
                                    <span
                                        className={`rest-order-note__badge${check ? ' rest-order-note__badge--done' : ''}`}
                                    >
                                        {check ? 'تأیید شد' : 'نیاز به تأیید'}
                                    </span>
                                ) : null}
                            </div>

                            <blockquote className="rest-order-note__quote">
                                {order.notes}
                            </blockquote>

                            {showAcceptActions ? (
                                <button
                                    type="button"
                                    className={`rest-order-note__confirm${check ? ' rest-order-note__confirm--checked' : ''}`}
                                    onClick={() => setCheck((value) => !value)}
                                    aria-pressed={check}
                                >
                                    <span className="rest-order-note__checkbox" aria-hidden="true">
                                        {check ? <TickIcon/> : null}
                                    </span>
                                    <span className="rest-order-note__confirm-text">
                                        {check
                                            ? 'توضیحات مشتری را خواندم و تأیید می‌کنم'
                                            : 'تأیید می‌کنم که توضیحات مشتری را خوانده‌ام'}
                                    </span>
                                </button>
                            ) : null}
                        </div>
                    )}
                    <div className="flex items-center justify-between pb-4 border-b border-b-gray-300">
                        <p className="text-sm text-[#353535]">جمع اقلام فاکتور</p>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-[#353535]">{Number(order?.total_price).toLocaleString()}</p>
                            <p className="text-sm text-[#353535]">تومان</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between pb-4 border-b border-b-gray-300">
                        <p className="text-sm text-[#353535]">تخفیف</p>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-[#353535]">{Number(order?.discount).toLocaleString()}</p>
                            <p className="text-sm text-[#353535]">تومان</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between pb-4 border-b border-b-gray-300">
                        <p className="text-sm text-[#353535]">هزینه ارسال</p>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-[#353535]">{Number(order?.send_price).toLocaleString()}</p>
                            <p className="text-sm text-[#353535]">تومان</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between ">
                        <p className="text-lg text-[#353535]">مبلغ قابل پرداخت</p>
                        <div className="flex items-center gap-2">
                            <p className="text-lg font-medium text-Primary-950">{Number(order?.total_amount).toLocaleString()}</p>
                            <p className="text-lg text-Primary-950">تومان</p>
                        </div>
                    </div>
                </div>

                {/* روش پرداخت و زمان تحویل */}
                <div
                    className="grid sm:grid-cols-2 grid-cols-1 items-center justify-center border border-gray-300 rounded-xl w-full p-6 gap-8">
                    <div className="flex items-center justify-center gap-4">
                        <Radio/>
                        <p className="text-[#717171] text-sm pt-1">
                            {order?.payment_method === "online" ? "پرداخت آنلاین" : "پرداخت در محل"}
                        </p>
                        <Card/>
                    </div>
                    <div className="flex items-center justify-center gap-4">
                        <Radio/>
                        <div className="flex gap-2">
                            <p className="text-[#717171] text-sm">زمان تحویل :</p>
                            <p className="text-[#717171] text-sm">
                                {order?.time === "now" ? "سریعترین زمان ممکن" : order?.time}
                            </p>
                        </div>
                        <Clock className="fill-[#717171]"/>
                    </div>
                </div>

                {!!order?.admin_note && (
                    <div
                        className="flex flex-col gap-4 border border-success-500 border-dashed rounded-xl w-full p-6 bg-success-50">
                        <p className="text-success-700 font-medium">{order.admin_note}</p>
                    </div>
                )}

                {paymentIncomplete && (
                    <div className="rest-order-note-alert" role="alert">
                        <span className="rest-order-note-alert__icon" aria-hidden="true">!</span>
                        <p>پرداخت این سفارش هنوز تکمیل نشده است. تا تکمیل پرداخت توسط مشتری، امکان تأیید سفارش وجود ندارد.</p>
                    </div>
                )}

                {showAcceptActions && (
                    <>
                        {!!order?.notes && !noteAcknowledged && (
                            <div className="rest-order-note-alert" role="alert">
                                <span className="rest-order-note-alert__icon" aria-hidden="true">!</span>
                                <p>برای تأیید سفارش، ابتدا توضیحات مشتری را در بخش فاکتور تأیید کنید.</p>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                            <ModalTime
                                noteAcknowledged={noteAcknowledged}
                                hasNote={!!order?.notes}
                                id={id}
                                time={order?.time}
                                refetch={refetch}
                                isPrint={print}
                                label="تایید و چاپ"
                            />
                            <ModalTime
                                hasNote={!!order?.notes}
                                noteAcknowledged={noteAcknowledged}
                                id={id}
                                time={order?.time}
                                refetch={refetch}
                                label="تایید"
                            />
                        </div>
                    </>
                )}

                {/* دکمه چاپ سفارش (همیشه نمایش داده میشه) */}
                <Button
                    type="button"
                    onPress={print}
                    className="text-center bg-white text-Primary-950 border border-Primary-950 w-full py-3 text-lg font-medium"
                >
                    چاپ سفارش
                </Button>
            </div>
        </>
    );
};

export default PrivateOrder;
